import React, { useRef, useMemo } from 'react';
import { useFrame, extend } from '@react-three/fiber';
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { ImprovedNoise } from 'three/examples/jsm/math/ImprovedNoise';

extend({ Line2, LineMaterial, LineGeometry });

interface EllipseConfig {
    baseCenter: [number, number];
    xRadius: number;
    yRadius: number;
    rotation: number;
    color: number;
    offset: number;
}

const EllipseCollection: React.FC<{ count?: number }> = ({ count = 5 }) => {
    const refs = useRef<any[]>([]);
    const noise = useMemo(() => new ImprovedNoise(), []);

    const configs = useMemo((): EllipseConfig[] =>
        Array.from({ length: count }, (_, i) => ({
            baseCenter: [(Math.random() - 0.5) * 16, (Math.random() - 0.5) * 10],
            xRadius: 2 + Math.random() * 3,
            yRadius: 1 + Math.random() * 2,
            rotation: Math.random() * Math.PI * 2,
            color: Math.random() * 0xffffff,
            offset: i * 100,
        })), [count]);

    const initial = useMemo(() =>
        configs.map(cfg => {
            const curve = new THREE.EllipseCurve(
                cfg.baseCenter[0], cfg.baseCenter[1],
                cfg.xRadius, cfg.yRadius,
                0, Math.PI * 2, false, cfg.rotation
            );
            return curve.getPoints(128).flatMap(p => [p.x, p.y, 0]);
        }), [configs]);

    useFrame(({ clock, size }) => {
        const t = clock.getElapsedTime();

        configs.forEach((cfg, i) => {
            const speed = 0.1;
            const nx = noise.noise(t * speed + cfg.offset, 0, 0);
            const ny = noise.noise(0, t * speed + cfg.offset, 0);

            const cx = cfg.baseCenter[0] + nx * 4;
            const cy = cfg.baseCenter[1] + ny * 3;

            const rX = cfg.xRadius + Math.sin(t * 0.5 + i) * 0.7;
            const rY = cfg.yRadius + Math.cos(t * 0.7 + i) * 0.5;

            const curve = new THREE.EllipseCurve(cx, cy, rX, rY, 0, Math.PI * 2, false, cfg.rotation);
            const pts = curve.getPoints(128).flatMap(p => [p.x, p.y, 0]);

            const line = refs.current[i];
            if (line) {
                line.geometry.setPositions(pts);
                line.material.resolution.set(size.width, size.height);
            }
        });
    });

    return (
        <>
            {configs.map((cfg, i) => (
                <line2 key={i} ref={el => refs.current[i] = el}>
                    <lineGeometry attach="geometry" positions={initial[i]} />
                    <lineMaterial attach="material" color={cfg.color} linewidth={3} />
                </line2>
            ))}
        </>
    );
};

export default EllipseCollection;